import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Modal, StyleSheet, Text, View } from 'react-native';

import { SUPPORTED_MODAL_ORIENTATIONS } from '../../../store/types';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { getSafeAreaSidePadding } from '../../../utils/safeArea';
import { TouchableOpacity } from '../common/Touchable';

interface GameRecoveryModalProps {
	visible: boolean;
	onRetry?: () => void;
	autoRetry?: boolean;
	maxRetries?: number;
}

export const GameRecoveryModal: React.FC<GameRecoveryModalProps> = ({
	visible,
	onRetry,
	autoRetry = true,
	maxRetries = 5,
}) => {
	const { t } = useTranslation();
	const [retryCount, setRetryCount] = useState(0);
	const [countdown, setCountdown] = useState(5);
	const { insets } = useSafeViewport();
	const safeOverlayInsets = getSafeAreaSidePadding(insets);

	useEffect(() => {
		if (!visible) {
			setRetryCount(0);
			setCountdown(5);
			return;
		}

		if (!autoRetry) return;
		const countdownInterval = setInterval(() => {
			setCountdown(prev => {
				if (prev <= 1) {
					if (retryCount < maxRetries) {
						setRetryCount(c => c + 1);
						onRetry?.();
						return 5; // Reset countdown
					}
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(countdownInterval);
	}, [visible, retryCount, autoRetry, maxRetries, onRetry]);

	if (!visible) return null;

	const canRetry = retryCount < maxRetries;

	return (
		<Modal
			transparent
			visible={visible}
			animationType='fade'
			presentationStyle='overFullScreen'
			supportedOrientations={SUPPORTED_MODAL_ORIENTATIONS}
			statusBarTranslucent
			navigationBarTranslucent
		>
			<View style={[styles.overlay, safeOverlayInsets]}>
				<View style={styles.modal}>
					<ActivityIndicator size='large' color='#FFD700' />

					<Text style={styles.title}>
						{t('T_SITE_GAME_RECOVERING', { defaultValue: 'Game Recovering' })}
					</Text>

					<Text style={styles.message}>
						{t('T_SITE_GAME_RECOVERY_UNAVAILABLE', {
							defaultValue:
								'Service is temporarily unavailable.\nPlease wait while we reconnect...',
						})}
					</Text>

					{autoRetry && canRetry && (
						<Text style={styles.retryInfo}>
							{t('T_SITE_GAME_RECOVERY_RETRY', {
								defaultValue: 'Retry attempt {{attempt}}/{{max}}\nNext retry in {{seconds}}s...',
								attempt: retryCount + 1,
								max: maxRetries,
								seconds: countdown,
							})}
						</Text>
					)}

					{!canRetry && (
						<Text style={styles.message}>
							{t('T_SITE_GAME_RECOVERY_ALMOST', { defaultValue: 'Almost there...' })}
							{'\n'}
						</Text>
					)}

					<View style={styles.buttonContainer}>
						{canRetry && !autoRetry && (
							<TouchableOpacity
								style={styles.retryButton}
								onPress={() => {
									setRetryCount(c => c + 1);
									onRetry?.();
								}}
							>
								<Text style={styles.buttonText}>
									{t('T_SITE_GAME_RETRY_NOW', { defaultValue: 'Retry Now' })}
								</Text>
							</TouchableOpacity>
						)}
					</View>
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.8)',
		justifyContent: 'center',
		alignItems: 'center',
	},
	modal: {
		backgroundColor: '#2a2a2a',
		borderRadius: 20,
		padding: 30,
		width: '80%',
		maxWidth: 400,
		alignItems: 'center',
		borderWidth: 2,
		borderColor: '#FFD700',
	},
	title: {
		fontSize: 24,
		fontWeight: 'bold',
		color: '#FFD700',
		marginTop: 20,
		marginBottom: 10,
	},
	message: {
		fontSize: 16,
		color: '#fff',
		textAlign: 'center',
		marginBottom: 20,
		lineHeight: 24,
	},
	retryInfo: {
		fontSize: 14,
		color: '#aaa',
		textAlign: 'center',
		marginBottom: 20,
		lineHeight: 20,
	},
	errorText: {
		fontSize: 14,
		color: '#ff6b6b',
		textAlign: 'center',
		marginBottom: 20,
		lineHeight: 20,
	},
	buttonContainer: {
		flexDirection: 'row',
		gap: 10,
	},
	retryButton: {
		backgroundColor: '#4CAF50',
		paddingHorizontal: 20,
		paddingVertical: 12,
		borderRadius: 10,
	},
	cancelButton: {
		backgroundColor: '#666',
		paddingHorizontal: 20,
		paddingVertical: 12,
		borderRadius: 10,
	},
	buttonText: {
		color: '#fff',
		fontSize: 16,
		fontWeight: 'bold',
	},
});
